import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, Rate, Checkbox } from 'antd';
import { UserRating } from '../../services/api';
import type {
  CreateUserRatingRequest
} from '../../models/enums';
import { UserRatingType } from '../../models/enums';
import { AutoResizeTextArea } from '../AutoResizeTextArea';
import { notification } from '../../services';
import styles from './ratingModal.scss';
import { useSelector } from 'react-redux';
import { selectUserInfo } from '../../routes/Auth/auth.ducks';
import { checkEmptyObject } from '../../services';
import history from '../../history';

interface RatingModalProps {
  visible: boolean;
  preselectedAdId?: string;
  preselectedChatId?: string;
  userId?: string;
  onCancel: () => void;
  onSuccess: () => void;
  form?: any;
}

const RatingModal: React.FC<RatingModalProps> = ({
  visible,
  preselectedAdId,
  preselectedChatId,
  userId,
  onCancel,
  onSuccess,
  form
}) => {
  const [selectedAd, setSelectedAd] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [commentLength, setCommentLength] = useState(0);
  const [eligibilityAds, setEligibilityAds] = useState([]);
  const [confirmed, setConfirmed] = useState(false);
  const userInfo = useSelector(selectUserInfo);

  // Fetch eligibility ads when modal opens if requested
  useEffect(() => {
    const fetchEligibility = async () => {
      try {
        setLoading(true);
        const response = await UserRating.getRatingEligibilityAds(userId);
        setEligibilityAds(response);
      } catch (error) {
        console.error('Error fetching rating eligibility:', error);
        setEligibilityAds([]);
      } finally {
        setLoading(false);
      }
    };

    if (
      visible &&
      userId &&
      checkEmptyObject(userInfo)
    ) {
      fetchEligibility();
    } else {
      setEligibilityAds([]);
      setLoading(false);
    }
  }, [visible, userId, userInfo]);


  // Auto-select ad when modal opens with preselectedAdId or preselectedChatId
  useEffect(() => {
    if (visible && eligibilityAds && eligibilityAds.length > 0) {
      let adToSelect = null;

      // Try to find by chatId first (more specific to the conversation)
      if (preselectedChatId) {
        adToSelect = eligibilityAds.find(ad => String(ad.chatId) === String(preselectedChatId));
      }

      // Fallback to adId if chatId didn't work
      if (!adToSelect && preselectedAdId) {
        adToSelect = eligibilityAds.find(ad => String(ad.adId) === String(preselectedAdId));
      }

      // If preselection was found, select it and clear the history state
      if (adToSelect) {
        setSelectedAd(adToSelect);
        form.setFieldsValue({ adId: adToSelect.adId });

        history.replace({
          pathname: history.location.pathname,
          state: {}
        });
      } else if (!selectedAd) {
        // Only select first ad as fallback if nothing is currently selected
        adToSelect = eligibilityAds[0];
        setSelectedAd(adToSelect);
        form.setFieldsValue({ adId: adToSelect.adId });
      }
    }

    // Reset comment length when modal visibility changes
    if (!visible) {
      setCommentLength(0);
    }
  }, [visible, preselectedAdId, preselectedChatId, eligibilityAds]);

  const handleAdSelect = (adId: string) => {
    const ad = eligibilityAds.find(a => a.adId === adId);

    // Reset all form fields except adId
    const fieldsToReset = [
      'adDescriptionAccuracy',
      'paymentAndExchangeAgreement',
      'communication',
      'honoringAgreement',
      'comment'
    ];

    form.resetFields(fieldsToReset);
    form.setFieldsValue({
      adDescriptionAccuracy: 0,
      paymentAndExchangeAgreement: 0,
      communication: 0,
      honoringAgreement: 0,
      comment: ''
    });
    setSelectedAd(ad || null);
    setCommentLength(0);
    setConfirmed(false);
  };

  const handleSubmit = () => {
    form.validateFields((err, values) => {
      if (err) {
        return;
      }

      if (!selectedAd) {
        return;
      }

      setSubmitting(true);

      const isSellerRating = selectedAd.userRatingType === UserRatingType.sellerRating;

      const requestData: CreateUserRatingRequest = {
        chatId: selectedAd.chatId,
        type: selectedAd.userRatingType,
        ...(isSellerRating
          ? { adDescriptionAccuracy: values.adDescriptionAccuracy }
          : { paymentAndExchangeAgreement: values.paymentAndExchangeAgreement }),
        communication: values.communication,
        honoringAgreement: values.honoringAgreement,
        comment: values.comment
      };

      UserRating.create(requestData)
        .then(() => {
          form.resetFields();
          setSelectedAd(null);
          setCommentLength(0);
          setConfirmed(false);
          setSubmitting(false);
          onSuccess();
        })
        .catch(() => {
          setSubmitting(false);
        });
    });
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedAd(null);
    setCommentLength(0);
    setConfirmed(false);
    onCancel();
  };

  return (
    <Modal
      visible={visible}
      onCancel={handleCancel}
      onOk={handleSubmit}
      okText="Ocijeni"
      cancelText="Odustani"
      className={styles.modal}
      okButtonProps={{ loading: submitting, disabled: !confirmed }}
      confirmLoading={submitting}
      centered
      bodyStyle={{ maxHeight: "calc(100dvh - 200px)", overflowY: "auto" }}
    >
      <div className={styles.title}>Ocijeni</div>
      <Form layout="vertical" className={styles.form}>
        <span className={styles.fieldLabel}>
          Oglas za koji ostavljate ocjenu
        </span>
        <Form.Item>
          {form.getFieldDecorator('adId', {
            rules: [{ required: true, message: 'Molimo odaberite oglas' }]
          })(
            <Select
              placeholder="Oglas"
              onChange={handleAdSelect}
              showSearch
              optionFilterProp="children"
              dropdownMatchSelectWidth={false}
              dropdownStyle={{ zIndex: 10000, maxWidth: '100%', width: '100%' }}
              getPopupContainer={(trigger) => trigger.parentElement}
              loading={loading}
              disabled={loading}
              className={styles.adSelect}
            >
              {eligibilityAds && eligibilityAds.length > 0 && eligibilityAds.map(ad => (
                <Select.Option key={ad.adId} value={ad.adId} title={ad.adTitle}>
                  {ad.adTitle}
                </Select.Option>
              ))}
            </Select>
          )}
        </Form.Item>

        {selectedAd && (
          <React.Fragment>
            {/* First rating field - different field for seller vs buyer */}
            {selectedAd.userRatingType === UserRatingType.sellerRating ? (
              <div className={styles.ratingRow}>
                <span className={styles.ratingLabel}>Je li opis oglasa bio točan?</span>
                <Form.Item className={styles.ratingFormItem}>
                  {form.getFieldDecorator('adDescriptionAccuracy', {
                    rules: [{
                      validator: (rule, value, callback) => {
                        if (!value || value === 0) {
                          callback('Ovo polje je obavezno');
                        } else {
                          callback();
                        }
                      }
                    }]
                  })(
                    <Rate className={styles.starRating} />
                  )}
                </Form.Item>
              </div>
            ) : (
              <div className={styles.ratingRow}>
                <span className={styles.ratingLabel}>Je li plaćanje ili zamjena prošla prema dogovoru?</span>
                <Form.Item className={styles.ratingFormItem}>
                  {form.getFieldDecorator('paymentAndExchangeAgreement', {
                    rules: [{
                      validator: (rule, value, callback) => {
                        if (!value || value === 0) {
                          callback('Ovo polje je obavezno');
                        } else {
                          callback();
                        }
                      }
                    }]
                  })(
                    <Rate className={styles.starRating} />
                  )}
                </Form.Item>
              </div>
            )}

            <div className={styles.ratingRow}>
              <span className={styles.ratingLabel}>
                {selectedAd.userRatingType === UserRatingType.sellerRating
                  ? 'Kakva je bila komunikacija s prodavačem?'
                  : 'Kakva je bila komunikacija s kupcem?'}
              </span>
              <Form.Item className={styles.ratingFormItem}>
                {form.getFieldDecorator('communication', {
                  rules: [{
                    validator: (rule, value, callback) => {
                      if (!value || value === 0) {
                        callback('Ovo polje je obavezno');
                      } else {
                        callback();
                      }
                    }
                  }]
                })(
                  <Rate className={styles.starRating} />
                )}
              </Form.Item>
            </div>

            <div className={styles.ratingRow}>
              <span className={styles.ratingLabel}>
                {selectedAd.userRatingType === UserRatingType.sellerRating
                  ? 'Je li prodavač ispoštovao dogovor?'
                  : 'Je li kupac ispoštovao dogovor?'}
              </span>
              <Form.Item className={styles.ratingFormItem}>
                {form.getFieldDecorator('honoringAgreement', {
                  rules: [{
                    validator: (rule, value, callback) => {
                      if (!value || value === 0) {
                        callback('Ovo polje je obavezno');
                      } else {
                        callback();
                      }
                    }
                  }]
                })(
                  <Rate className={styles.starRating} />
                )}
              </Form.Item>
            </div>

            <span className={styles.fieldLabel}>
              Dodatni komentar <span className={styles.required}>*</span>
            </span>
            <Form.Item className={styles.commentFormItem}>
              {form.getFieldDecorator('comment', {
                rules: [
                  { required: true, message: 'Komentar je obavezan' },
                  { max: 600, message: 'Komentar može imati maksimalno 600 znakova' },
                  {
                    validator: (rule, value, callback) => {
                      if (value && value.trim().length === 0) {
                        callback('Komentar je obavezan');
                      } else {
                        callback();
                      }
                    }
                  }
                ]
              })(
                <AutoResizeTextArea
                  placeholder={selectedAd.userRatingType === UserRatingType.sellerRating
                    ? 'Napišite svoje iskustvo s prodavačem...'
                    : 'Napišite svoje iskustvo s kupcem...'}
                  maxLength={600}
                  onChange={(e) => setCommentLength(e.target.value.length)}
                  autoSize={{ minRows: 4, maxRows: 10 }}
                />
              )}
              <div className={styles.characterCounter}>
                {commentLength} / 600
              </div>
            </Form.Item>

            <div className={styles.confirmSection}>
              <Checkbox
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className={styles.confirmCheckbox}
              >
                Potvrđujem da je kupoprodaja završena te dopuštam uvid u komunikaciju vezanu uz ovaj oglas.
              </Checkbox>
              <div className={styles.noticeText}>
                Jednom ostavljena ocjena ne može biti promijenjena.
              </div>
            </div>
          </React.Fragment>
        )}
      </Form>
    </Modal>
  );
};

export default Form.create()(RatingModal);



// WEBPACK FOOTER //
// ./src/components/RatingModal/index.tsx